import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { highlightLpql } from '@/lib/lpql-highlight';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="p-4 bg-gray-950 text-gray-100 text-sm overflow-x-auto font-mono leading-relaxed">
        <code>{highlightLpql(children.trim())}</code>
      </pre>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'search-expressions', label: 'Search Expressions' },
  { id: 'field-expressions', label: 'Field Expressions', level: 1 },
  { id: 'text-search', label: 'Text Search', level: 1 },
  { id: 'boolean-logic', label: 'Boolean Logic', level: 1 },
  { id: 'in-operator', label: 'IN Operator', level: 1 },
  { id: 'time-ranges', label: 'Time Ranges' },
  { id: 'index-time', label: 'Index Time', level: 1 },
  { id: 'known-fields', label: 'Known Fields' },
  { id: 'json-paths', label: 'JSON Paths' },
  { id: 'pipe-commands', label: 'Pipe Commands' },
  { id: 'stats', label: 'stats', level: 1 },
  { id: 'timechart', label: 'timechart', level: 1 },
  { id: 'chart', label: 'chart', level: 1 },
  { id: 'mstats', label: 'mstats', level: 1 },
  { id: 'mpreview', label: 'mpreview', level: 1 },
  { id: 'eval', label: 'eval', level: 1 },
  { id: 'where', label: 'where', level: 1 },
  { id: 'table', label: 'table', level: 1 },
  { id: 'top-rare', label: 'top / rare', level: 1 },
  { id: 'dedup', label: 'dedup', level: 1 },
  { id: 'sort', label: 'sort', level: 1 },
  { id: 'head-tail', label: 'head / tail', level: 1 },
  { id: 'fields', label: 'fields', level: 1 },
  { id: 'rename', label: 'rename', level: 1 },
  { id: 'rex', label: 'rex', level: 1 },
  { id: 'search', label: 'search', level: 1 },
  { id: 'fillnull-filldown', label: 'fillnull / filldown', level: 1 },
  { id: 'bin', label: 'bin / bucket', level: 1 },
  { id: 'join', label: 'join', level: 1 },
  { id: 'makeresults', label: 'makeresults', level: 1 },
  { id: 'transpose', label: 'transpose', level: 1 },
  { id: 'lookup', label: 'lookup', level: 1 },
  { id: 'inputlookup-outputlookup', label: 'inputlookup / outputlookup', level: 1 },
  { id: 'threatintel', label: 'threatintel / iplookup', level: 1 },
  { id: 'iplocation', label: 'iplocation', level: 1 },
  { id: 'eval-functions', label: 'Eval Functions' },
];

export function LpqlSyntaxPage() {
  useDocumentMeta({ title: 'LPQL Syntax Reference: LogPulse Docs', description: 'Complete reference for LogPulse Query Language (LPQL): field expressions, boolean logic, pipe commands, and aggregations.' });

  const t = useT();

  return (
    <div className="flex gap-8">
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-2">
            {t('docs.nav.search')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {t('docs.lpql.title')}
          </h1>
          <P>{t('docs.lpql.intro')}</P>
        </div>

        {/* Overview */}
        <Section id="overview" title={t('docs.lpql.overview.title')}>
          <P>{t('docs.lpql.overview.description')}</P>
          <CodeBlock title="Query structure">{`[search] <expression> [earliest=<time>] [latest=<time>] [| command1] [| command2] ...`}</CodeBlock>
          <P>{t('docs.lpql.overview.examples')}</P>
          <CodeBlock title="Examples">{`level=error
level=error source=api host=prod-*
(level=error OR level=fatal) source=payment earliest=-1h
level=error | stats count by host | sort -count | head 10`}</CodeBlock>
        </Section>

        {/* Search Expressions */}
        <Section id="search-expressions" title={t('docs.lpql.searchExpr.title')}>
          <SubSection id="field-expressions" title={t('docs.lpql.fieldExpr.title')}>
            <P>{t('docs.lpql.fieldExpr.description')}</P>
            <Table
              headers={['Operator', 'Description', 'Example']}
              rows={[
                ['= or ==', 'Exact match', 'level=error'],
                ['!=', 'Not equal', 'status!=200'],
                ['>', 'Greater than', 'duration>1000'],
                ['<', 'Less than', 'status<400'],
                ['>=', 'Greater or equal', 'host>=prod-01'],
                ['<=', 'Less or equal', 'latency<=100'],
              ]}
            />
            <CodeBlock>{`level=error
status!=200
duration>1000
host>=prod-01
field="quoted value"`}</CodeBlock>
            <P>{t('docs.lpql.fieldExpr.wildcards')}</P>
            <CodeBlock>{`source=api-*       # starts with "api-"
host=*prod*        # contains "prod"
field=*            # field exists and is non-empty
field!=*           # field does not exist or is empty`}</CodeBlock>
          </SubSection>

          <SubSection id="text-search" title={t('docs.lpql.textSearch.title')}>
            <P>{t('docs.lpql.textSearch.description')}</P>
            <CodeBlock>{`error                     # free text search
"connection refused"      # exact phrase (double quotes)
'timeout error'           # exact phrase (single quotes)
*exception*               # wildcard text search`}</CodeBlock>
          </SubSection>

          <SubSection id="boolean-logic" title={t('docs.lpql.booleanLogic.title')}>
            <P>{t('docs.lpql.booleanLogic.description')}</P>
            <CodeBlock>{`# Implicit AND (space-separated)
level=error source=api

# Explicit operators
level=error AND source=api
level=error OR level=warn
NOT debug

# Grouping with parentheses
(level=error OR level=fatal) AND source=api

# Operator precedence: NOT > AND > OR
level=error OR level=warn NOT debug`}</CodeBlock>
          </SubSection>

          <SubSection id="in-operator" title={t('docs.lpql.inOperator.title')}>
            <P>{t('docs.lpql.inOperator.description')}</P>
            <CodeBlock>{`level IN (error, fatal, critical)
source IN ("api-gateway", "web-server")
host IN (prod-*, dev-*)`}</CodeBlock>
          </SubSection>
        </Section>

        {/* Time Ranges */}
        <Section id="time-ranges" title={t('docs.lpql.timeRanges.title')}>
          <P>{t('docs.lpql.timeRanges.description')}</P>
          <Table
            headers={['Unit', 'Meaning', 'Example']}
            rows={[
              ['m', 'Minutes', 'earliest=-15m'],
              ['h', 'Hours', 'earliest=-1h'],
              ['d', 'Days', 'earliest=-7d'],
              ['w', 'Weeks', 'earliest=-1w'],
              ['M', 'Months', 'earliest=-1M'],
              ['y', 'Years', 'earliest=-1y'],
            ]}
          />
          <CodeBlock>{`# Relative time
earliest=-1h
earliest=-15m latest=now

# Absolute time
earliest=2024-01-01T00:00:00Z latest=2024-12-31T23:59:59Z

# Combined with search
earliest=-1h error status>500
level=error earliest=-24h latest=-1h`}</CodeBlock>

          <SubSection id="index-time" title={t('docs.lpql.indexTime.title')}>
            <P>{t('docs.lpql.indexTime.description')}</P>
            <P>{t('docs.lpql.indexTime.filters')}</P>
            <CodeBlock>{`# Everything indexed in the last 15 minutes, whatever its event time
earliest=-7d _index_earliest=-15m

# Late arrivals: indexed in the last hour, but more than an hour old
earliest=-24h _index_earliest=-1h | where _indextime-_time > 3600

# Indexing delay per source
earliest=-1h | eval lag=_indextime-_time | stats avg(lag) max(lag) by source

# Show the index time as a column
earliest=-15m | eval indexed=_indextime | table _time, indexed, source`}</CodeBlock>
            <P>{t('docs.lpql.indexTime.note')}</P>
          </SubSection>
        </Section>

        {/* Known Fields */}
        <Section id="known-fields" title={t('docs.lpql.knownFields.title')}>
          <P>{t('docs.lpql.knownFields.description')}</P>
          <Table
            headers={['Field', 'Description']}
            rows={[
              ['event', 'Log message / event text'],
              ['level', 'Log level (debug, info, warn, error, fatal)'],
              ['index', 'Index name'],
              ['source', 'Source / file name'],
              ['sourcetype', 'Source type'],
              ['host', 'Hostname'],
              ['_time / timestamp', 'Event time (from the event when a parser reads it)'],
              ['_indextime', 'When LogPulse indexed the event; hidden until named'],
              ['cluster', 'Kubernetes cluster'],
              ['namespace', 'Kubernetes namespace'],
              ['pod', 'Kubernetes pod'],
              ['container', 'Kubernetes container'],
              ['node', 'Kubernetes node'],
            ]}
          />
          <P>{t('docs.lpql.knownFields.mapColumns')}</P>
          <CodeBlock>{`# Map columns use dot notation
labels.app="my-service"
annotations.owner="platform-team"
attributes.status="active"
parsed_fields.request_id="abc-123"`}</CodeBlock>
        </Section>

        {/* JSON Paths */}
        <Section id="json-paths" title={t('docs.lpql.jsonPaths.title')}>
          <P>{t('docs.lpql.jsonPaths.description')}</P>
          <Table
            headers={['Syntax', 'Meaning', 'Example']}
            rows={[
              ['a.b.c', 'Walk into nested objects', 'user.profile.country'],
              ['a[N]', 'Array index (0-based)', 'io_data[0].value'],
              ['a[*]', 'Any array item', 'io_data[*].name'],
              ['a[k="v"]', 'Filter array by predicate', 'io_data[io="239"].value'],
            ]}
          />
          <P>{t('docs.lpql.jsonPaths.searchUsage')}</P>
          <CodeBlock title="Search / where filters">{`# Filter events by a nested value
io_data[io="239"].name=Movement
user.profile.country=NL

# Combine with boolean logic
level=error AND io_data[io="66"].value > 0

# In the where pipe (eval expressions)
| where io_data[io="66"].value > 0
| where user.profile.country != "NL"`}</CodeBlock>
          <P>{t('docs.lpql.jsonPaths.evalUsage')}</P>
          <CodeBlock title="eval: compute new fields from nested values">{`# Extract a nested value into a top-level field
| eval voltage = io_data[io="66"].value

# Compute with it
| eval voltage_ok = if(io_data[io="66"].value >= 12, "ok", "low")

# Combine paths from multiple sources
| eval full = user.profile.country . "-" . io_data[0].name`}</CodeBlock>
          <P>{t('docs.lpql.jsonPaths.statsUsage')}</P>
          <CodeBlock title="stats / timechart: aggregate nested values">{`# Latest voltage reading per device
| stats latest(io_data[io="66"].value) as voltage by device_id

# Average temperature over time
| timechart span=1h avg(sensors[type="temp"].reading)

# Group by a nested value
| stats count by user.profile.country`}</CodeBlock>
          <P>{t('docs.lpql.jsonPaths.tableUsage')}</P>
          <CodeBlock title="table: display nested values as columns">{`# Project nested values as columns
| table timestamp, device_id, io_data[io="66"].value

# Mix with dot-notation and regular fields
| table host, user.profile.country, status`}</CodeBlock>
          <p className="text-xs text-gray-500 dark:text-gray-500 italic">{t('docs.lpql.jsonPaths.note')}</p>
        </Section>

        {/* Pipe Commands */}
        <Section id="pipe-commands" title={t('docs.lpql.pipeCommands.title')}>
          <P>{t('docs.lpql.pipeCommands.description')}</P>

          <SubSection id="stats" title="stats">
            <P>{t('docs.lpql.stats.description')}</P>
            <CodeBlock>{`| stats count by level
| stats avg(duration) as avg_dur by source
| stats count, sum(amount), avg(price) by level, host
| stats dc(host) as unique_hosts by level
| stats latest(io_data[io="66"].value) as voltage by device_id`}</CodeBlock>
            <P>{t('docs.lpql.stats.functions')}</P>
            <Table
              headers={['Category', 'Functions']}
              rows={[
                ['Count', 'count, c'],
                ['Average', 'avg, mean'],
                ['Sum', 'sum, sumsq'],
                ['Min/Max', 'min, max'],
                ['Distinct', 'dc, distinct_count'],
                ['Statistical', 'median, mode, stdev, stdevp, var, variance, range'],
                ['Selection', 'values, list, first, last, latest, earliest'],
                ['Percentile', 'perc50, p95, exactperc99, upperperc75'],
              ]}
            />
          </SubSection>

          <SubSection id="timechart" title="timechart">
            <P>{t('docs.lpql.timechart.description')}</P>
            <CodeBlock>{`| timechart count
| timechart span=5m count
| timechart span=1h count by level
| timechart span=1h cont=true avg(duration) by source`}</CodeBlock>
            <P>{t('docs.lpql.timechart.options')}</P>
            <Table
              headers={['Option', 'Description', 'Example']}
              rows={[
                ['span', 'Time bucket size', 'span=5m, span=1h, span=1d'],
                ['cont', 'Fill gaps with zeros', 'cont=true (default)'],
                ['by', 'Split by field (max 1)', 'by level'],
              ]}
            />
          </SubSection>

          <SubSection id="chart" title="chart">
            <P>{t('docs.lpql.chart.description')}</P>
            <CodeBlock>{`| chart count over source
| chart count over source by level
| chart avg(duration) over host limit=10`}</CodeBlock>
          </SubSection>

          <SubSection id="mstats" title="mstats">
            <P>{t('docs.lpql.mstats.description')}</P>
            <CodeBlock>{`| mstats avg(value) where metric_name=cpu_usage by host
| mstats span=5m max(value) where index=otlp metric_name=memory_bytes
| mstats rate(value) where metric_name=http_requests_total by route span=1m
| mstats p95(value) where metric_name=http_duration by route`}</CodeBlock>
          </SubSection>

          <SubSection id="mpreview" title="mpreview">
            <P>{t('docs.lpql.mpreview.description')}</P>
            <CodeBlock>{`| mpreview index=otlp metric_name=cpu_usage`}</CodeBlock>
          </SubSection>

          <SubSection id="eval" title="eval">
            <P>{t('docs.lpql.eval.description')}</P>
            <CodeBlock>{`| eval ratio = count / total
| eval msg = "Status: " . status
| eval is_error = (status >= 500)
| eval x=1, y=2, z=x+y
| eval voltage = io_data[io="66"].value`}</CodeBlock>
            <Table
              headers={['Category', 'Operators']}
              rows={[
                ['Arithmetic', '+ - * / %'],
                ['Comparison', '== != < > <= >= LIKE'],
                ['Boolean', 'AND OR NOT XOR'],
                ['String', '. (concatenation)'],
                ['Unary', '- (negation)'],
              ]}
            />
          </SubSection>

          <SubSection id="where" title="where">
            <P>{t('docs.lpql.where.description')}</P>
            <CodeBlock>{`| where status > 400
| where level == "error" OR level == "fatal"
| where duration/1000 > 5
| where like(source, "api%")
| where io_data[io="66"].value < 12`}</CodeBlock>
          </SubSection>

          <SubSection id="table" title="table">
            <P>{t('docs.lpql.table.description')}</P>
            <CodeBlock>{`| table level, event, host
| table timestamp, source_*, host
| table *
| table timestamp, device_id, io_data[io="66"].value`}</CodeBlock>
          </SubSection>

          <SubSection id="top-rare" title="top / rare">
            <P>{t('docs.lpql.topRare.description')}</P>
            <CodeBlock>{`| top level
| top 5 host
| top limit=10 showperc=false source
| top level by host

| rare level
| rare 5 host by source`}</CodeBlock>
          </SubSection>

          <SubSection id="dedup" title="dedup">
            <P>{t('docs.lpql.dedup.description')}</P>
            <CodeBlock>{`| dedup host
| dedup 3 level
| dedup host, source
| dedup keepevents=true sortby=timestamp host`}</CodeBlock>
          </SubSection>

          <SubSection id="sort" title="sort">
            <P>{t('docs.lpql.sort.description')}</P>
            <CodeBlock>{`| sort timestamp        # ascending (default)
| sort -level           # descending
| sort limit=100 -duration timestamp`}</CodeBlock>
          </SubSection>

          <SubSection id="head-tail" title="head / tail">
            <P>{t('docs.lpql.headTail.description')}</P>
            <CodeBlock>{`| head          # first 10 results (default)
| head 20       # first 20 results
| tail 50       # last 50 results`}</CodeBlock>
          </SubSection>

          <SubSection id="fields" title="fields">
            <P>{t('docs.lpql.fields.description')}</P>
            <CodeBlock>{`| fields level, event, host      # include only these
| fields +source                 # include
| fields -attributes             # exclude
| fields source_*, host          # wildcard include`}</CodeBlock>
          </SubSection>

          <SubSection id="rename" title="rename">
            <P>{t('docs.lpql.rename.description')}</P>
            <CodeBlock>{`| rename old_field as new_field
| rename level as log_level, host as hostname`}</CodeBlock>
          </SubSection>

          <SubSection id="rex" title="rex">
            <P>{t('docs.lpql.rex.description')}</P>
            <CodeBlock>{`| rex field=event "(?<app>\\w+)-(?<env>\\w+)"
| rex "(?<status_code>\\d{3})"`}</CodeBlock>
          </SubSection>

          <SubSection id="search" title="search">
            <P>{t('docs.lpql.search.description')}</P>
            <CodeBlock>{`| search level="error" OR status>500
| stats count by host | search count > 100`}</CodeBlock>
          </SubSection>

          <SubSection id="fillnull-filldown" title="fillnull / filldown">
            <P>{t('docs.lpql.fillnullFilldown.description')}</P>
            <CodeBlock>{`| fillnull value="0" count
| fillnull value="unknown" host, source

| sort timestamp | filldown host`}</CodeBlock>
          </SubSection>

          <SubSection id="bin" title="bin / bucket">
            <P>{t('docs.lpql.bin.description')}</P>
            <CodeBlock>{`| bin response_time_ms span=100 as latency_bucket
| bin timestamp span=1h as hour
| bin duration bins=10 | stats count by duration`}</CodeBlock>
          </SubSection>

          <SubSection id="join" title="join">
            <P>{t('docs.lpql.join.description')}</P>
            <CodeBlock>{`| join host [search source=metadata | table host, region]
| join type=left user_id [search index=_audit | stats count by user_id]`}</CodeBlock>
          </SubSection>

          <SubSection id="makeresults" title="makeresults">
            <P>{t('docs.lpql.makeresults.description')}</P>
            <CodeBlock>{`| makeresults | eval msg="hello"
| makeresults count=3 | eval host="test", level="info"`}</CodeBlock>
          </SubSection>

          <SubSection id="transpose" title="transpose">
            <P>{t('docs.lpql.transpose.description')}</P>
            <CodeBlock>{`| stats count by level | transpose
| timechart count by source | transpose header_field=_time`}</CodeBlock>
          </SubSection>

          <SubSection id="lookup" title="lookup">
            <P>{t('docs.lpql.lookup.description')}</P>
            <CodeBlock>{`| lookup users user_id OUTPUT name, email AS user_email
| lookup ip_allocations src_ip
| lookup assets key AS hostname OUTPUT owner, criticality
| lookup identities key AS actor_email OUTPUT roles, privileged
| lookup identities key AS actor_email OUTPUT roles | where NOT match(roles, "cloudflare_admin")`}</CodeBlock>
          </SubSection>

          <SubSection id="inputlookup-outputlookup" title="inputlookup / outputlookup">
            <P>{t('docs.lpql.inputOutputLookup.description')}</P>
            <CodeBlock>{`| inputlookup users
| inputlookup ip_allocations where region="EU" | stats count by region

index=apps level=error | stats count by service | outputlookup error_baseline
index=apps | stats count by host | outputlookup append=true host_counts`}</CodeBlock>
          </SubSection>

          <SubSection id="threatintel" title="threatintel / iplookup">
            <P>{t('docs.lpql.threatintel.description')}</P>
            <CodeBlock>{`| threatintel src_ip
| threatintel dest_ip OUTPUT threat_verdict AS verdict, threat_confidence
| threatintel src_ip | where threat_verdict = "malicious"`}</CodeBlock>
          </SubSection>

          <SubSection id="iplocation" title="iplocation">
            <P>{t('docs.lpql.iplocation.description')}</P>
            <CodeBlock>{`| iplocation
| iplocation client_ip | stats count by country
| iplocation src_ip | stats count by lat, lon
| iplocation src_ip | table src_ip, country, city, asn`}</CodeBlock>
          </SubSection>
        </Section>

        {/* Eval Functions */}
        <Section id="eval-functions" title={t('docs.lpql.evalFunctions.title')}>
          <P>{t('docs.lpql.evalFunctions.description')}</P>

          <Table
            headers={['Category', 'Functions']}
            rows={[
              ['Conditional', 'if(cond, a, b), case(...), coalesce(a, b, ...)'],
              ['String', 'lower, upper, len, substr, trim, ltrim, rtrim, replace, split, urldecode, urlencode'],
              ['Conversion', 'tostring, tonumber'],
              ['Math', 'round, ceil, floor, abs, pow, sqrt, log, ln, exp, pi, min, max'],
              ['Type checks', 'isnull, isnotnull, isnum, isstr, typeof, null'],
              ['Time', 'now, strftime, strptime, relative_time, time'],
              ['Pattern', 'match, like, searchmatch, cidrmatch'],
              ['Multivalue', 'mvindex, mvcount, mvjoin, mvappend, mvdedup, mvsort, mvfilter, mvfind'],
              ['Crypto', 'md5, sha1, sha256, sha512'],
            ]}
          />

          <CodeBlock title="Examples">{`| eval greeting = if(level=="error", "ALERT", "OK")
| eval name = lower(host)
| eval dur_sec = round(duration / 1000, 2)
| eval is_prod = like(host, "prod%")
| eval hash = md5(event)`}</CodeBlock>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
